import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useGroups } from "../lib/api";
import { normalizeGroupName } from "../lib/groupName";
import { Icon } from "../components/msd/Icon";
import { Btn, CARD, LinkBtn, PageHead } from "../components/msd/ui";
import { ChatHistoryPanel } from "../components/chat/ChatHistoryPanel";
import { VehiclePic, dayLabel, servicePeriod } from "./msd/history/parts";

// .hx — the column both views sit in
const COLUMN = "flex max-w-[1180px] flex-col gap-[18px] text-msd-ink";

/**
 * Browse groups and search by name, then read a selected group's history.
 * The group list is loaded once; opening a group's history is the only other
 * request, made by ChatHistoryPanel.
 */
export default function ChatHistorySearch() {
  const [searchParams] = useSearchParams();
  const groupParam = searchParams.get("group") || "";
  const [draft, setDraft] = useState(groupParam);
  const [query, setQuery] = useState(groupParam); // empty = show all authorized groups
  const [selected, setSelected] = useState(null); // { row, g } | null

  const { data, isLoading, isFetching, isError, refetch } = useGroups({ enabled: query !== null });
  const rows = useMemo(() => data?.rows || [], [data]);

  // Arriving from a "View chat history" link with ?group=<exact name>: skip
  // the results list and open that group directly once it loads.
  useEffect(() => {
    if (groupParam && rows.length && !selected) {
      const exact = rows.find((r) => r["Group Name"] === groupParam);
      if (exact) setSelected({ row: exact._row, g: exact });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupParam, rows.length]);

  const results = useMemo(() => {
    if (query === null) return null;
    const normalizedQ = normalizeGroupName(query);
    const q = query.trim().toLowerCase();
    const matchingRows = !normalizedQ && !q
      ? rows
      : rows.filter(
        (r) =>
          (r["Group Name"] || "").toLowerCase().includes(q) ||
          normalizeGroupName(r["Group Name"]).includes(normalizedQ) ||
          (r["Customer Name"] || "").toLowerCase().includes(q)
      );
    return [...matchingRows].sort((a, b) => {
      const createdAtDiff = Date.parse(b["Created At"] || "") - Date.parse(a["Created At"] || "");
      return (Number.isNaN(createdAtDiff) ? 0 : createdAtDiff) || Number(b._row) - Number(a._row);
    });
  }, [rows, query]);

  const submitSearch = () => {
    setSelected(null);
    setQuery(draft);
  };

  if (selected) {
    return (
      <div className={COLUMN} data-testid="chat-history-detail">
        <LinkBtn onClick={() => setSelected(null)} className="self-start !font-medium">
          <Icon name="back" size={16} stroke={2.2} />
          Chat history
        </LinkBtn>
        <ChatHistoryPanel row={selected.row} g={selected.g} />
      </div>
    );
  }

  return (
    <div className={COLUMN} data-testid="chat-history-search-page">
      <PageHead title="Chat history" sub="Every service conversation, including deleted groups" />

      <div className="flex h-[54px] max-w-[720px] items-center gap-3 rounded-2xl bg-msd-surface pl-[18px] pr-[10px] text-msd-muted shadow-msd-e2 focus-within:shadow-[0_0_0_2px_#2667B5]">
        <Icon name="search" size={20} className="shrink-0" />
        <input
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            setQuery(e.target.value);
          }}
          onKeyDown={(e) => e.key === "Enter" && submitSearch()}
          placeholder="Vehicle, group or customer name"
          aria-label="Search chat history"
          data-testid="chat-history-search-input"
          className="min-w-0 grow border-0 bg-transparent text-[17px] text-msd-ink outline-none placeholder:text-msd-muted"
        />
        <Btn variant="pri" size="sm" onClick={submitSearch} disabled={!draft.trim()} data-testid="chat-history-search-btn">
          Search
        </Btn>
      </div>

      {/* {query === null && (
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-msd-royal-soft text-msd-royal">
            <Icon name="chat" size={22} />
          </div>
          <h3 className="m-0 text-[17px] font-semibold text-msd-ink">Search for a group</h3>
        </div>
      )} */}

      {query !== null && (isLoading || isFetching) && (
        <div className="flex items-center justify-center py-16">
          <Loader2 size={20} className="animate-spin text-msd-faint" />
        </div>
      )}

      {query !== null && !isLoading && !isFetching && (
        <div className={`${CARD} min-w-0 px-2 py-[6px]`}>
          {!results.length && (
            <p className="m-0 p-10 text-center text-[15px] text-msd-muted">No conversations match &ldquo;{query}&rdquo;</p>
          )}
          {results.map((r) => {
            const sub = [r["Customer Name"], r["Vehicle Model"], r["Workshop Location"]].filter(Boolean).join(" · ");
            const period = servicePeriod(r);
            const deleted = dayLabel(r["WA Deleted At"]);
            return (
              <button
                key={r._row}
                type="button"
                onClick={() => setSelected({ row: r._row, g: r })}
                data-testid={`chat-history-result-${r._row}`}
                className="flex w-full items-center gap-4 rounded-[14px] border-0 bg-transparent p-3 text-left text-msd-ink hover:bg-msd-soft [&+&]:shadow-[0_-1px_0_var(--line)]"
              >
                <VehiclePic group={r} className="h-[54px] w-[72px] rounded-xl" />
                <span className="min-w-0 grow">
                  <b className="block truncate text-[17px] font-bold tracking-[-0.01em]">{r["Group Name"] || "Untitled group"}</b>
                  <span className="block truncate text-[13px] text-msd-muted">{sub || "—"}</span>
                </span>
                {(period || deleted) && (
                  <span className="flex flex-col items-end whitespace-nowrap text-[14px] max-[860px]:hidden">
                    {period ? <span>{period}</span> : null}
                    {deleted ? <span className="text-[13px] text-msd-muted">Group deleted {deleted}</span> : null}
                  </span>
                )}
                <span className="flex text-msd-faint">
                  <Icon name="right" size={18} stroke={2} />
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
